import { formatCardKey } from '@/ai/utils/helpers'
import { diagramHelpers } from '@/mixins/diagramHelpers'

export const examples = {
  workflowDiagram: [
    { role: 'Customer', domainEvent: 'Order created' },
    { role: 'Support', domainEvent: 'Issue resolved' },
    { role: 'Supplier', domainEvent: 'Delivery scheduled' }
  ],
  commandExampleCreateOrder: [
    { fieldName: 'id' },
    { fieldName: 'customerName' },
    { fieldName: 'productName' },
    { fieldName: 'deliveryTime' },
    { fieldName: 'deliveryAddress' },
    { fieldName: 'quantity' },
    { fieldName: 'comment' }
  ],
  commandExampleDeliverOrder: [
    { fieldName: 'id' },
    { fieldName: 'supplier' },
    { fieldName: 'productName' },
    { fieldName: 'deliveryTime' },
    { fieldName: 'deliveryAddress' },
    { fieldName: 'quantity' },
    { fieldName: 'shippingMethod' }
  ],
  readModelExampleOrder: [
    { fieldName: 'id' },
    { fieldName: 'customer', indexed: true },
    { fieldName: 'order' },
    { fieldName: 'delivery Location' }
  ],
  readModelExampleProduct: [
    { fieldName: 'id' },
    { fieldName: 'productName', indexed: true },
    { fieldName: 'productType', indexed: true }
  ],
  readModelExampleCustomer: [
    { fieldName: 'id' },
    { fieldName: 'fullName', indexed: true },
    { fieldName: 'address' },
    { fieldName: 'paymentMethod' }
  ],
  entityExampleCustomer: [
    {
      fieldName: 'id',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data'],
      primaryKey: true
    },
    {
      fieldName: 'customerName',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'customerAddress',
      tags: '',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'phoneNumber',
      tags: '',
      exampleData: ['Example data', 'Example data', 'Example data']
    }
  ],
  entityExampleProduct: [
    {
      fieldName: 'id',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data'],
      primaryKey: true
    },
    {
      fieldName: 'productName',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'productType',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'stockLevel',
      tags: '',
      exampleData: ['x', 'y', 'z']
    }
  ],
  exampleCategorizedFilePaths: {
    entityRelatedPaths: [
      'src/models/Order.js',
      'src/domain/user/UserAggregate.java'
    ],
    eventRelatedPaths: [
      'src/application/commands/PlaceOrderCommandHandler.py',
      'src/controllers/OrderController.ts',
      'src/graphql/mutations/CreateItem.js'
    ],
    commandRelatedPaths: [
      'src/controllers/OrderController.js',
      'src/services/UserService.java',
      'src/application/commands/PlaceOrderCommandHandler.py',
      'src/graphql/mutations/CreateItem.js'
    ],
    queryRelatedPaths: [
      'src/queries/ProductQueries.js',
      'src/infrastructure/repositories/CustomerRepository.java',
      'src/views/OrderSummaryView.ts',
      'src/routes/GetOrderSummary.ts',
      'src/controllers/GetOrderSummary.ts',
      'src/graphql/queries/GetItem.js'
    ],
    otherDomainRelatedPaths: []
  }
}

export const workflowFromCodeExample = {
  updatesAndAdditions: [
    {
      role: 'User',
      eventDescription: 'Created order',
      command: {
        name: 'Create order',
        dataFields: examples.commandExampleCreateOrder
      },
      queries: [
        {
          name: 'Get products list',
          dataFields: examples.readModelExampleOrder
        },
        {
          name: 'Get customer details',
          dataFields: examples.readModelExampleCustomer
        }
      ],
      entities: [
        {
          name: 'Customer',
          dataFields: [
            {
              fieldName: 'id',
              tags: 'REQUIRED',
              primaryKey: true
            },
            {
              fieldName: 'customerName',
              tags: 'REQUIRED'
            },
            {
              fieldName: 'customerAddress',
              tags: ''
            },
            {
              fieldName: 'phoneNumber',
              tags: ''
            }
          ]
        },
        {
          name: 'Product',
          dataFields: [
            {
              fieldName: 'id',
              tags: 'REQUIRED',
              primaryKey: true
            },
            {
              fieldName: 'productName',
              tags: 'REQUIRED'
            },
            {
              fieldName: 'productType',
              tags: 'REQUIRED'
            },
            {
              fieldName: 'stockLevel',
              tags: ''
            }
          ]
        }
      ],
      gwt: ['Given the customer has selected items, when they create an order, then the order should be saved in the system.']
    }
  ]
}

export const workflowDataExample = {
  events: [
    {
      id: 'event_1',
      description: 'Example description 1',
      laneId: 'lane_1',
      parentEventNames: ['start'],
      cards: [
        {
          id: 'card_1',
          description: 'Card 1',
          cardType: {
            id: 'card_type_1',
            title: 'Card type 1'
          }
        },
        {
          id: 'card_2',
          description: 'Card 2',
          cardType: {
            id: 'card_type_2',
            title: 'Card type 2'
          }
        }
      ],
      dataModels: [
        {
          name: 'Write model',
          type: 'Event Story',
          cardId: 'card_1',
          dataFields: [
            {
              name: 'Field 1',
              cardinality: 'one-to-one',
              exampleData: ['Example data', 'Example data', 'Example data'],
              relatedEntityId: 'entity_1'
            },
            {
              name: 'Field 2',
              cardinality: null,
              exampleData: null,
              relatedEntityId: null
            }
          ]
        },
        {
          name: 'Read Model',
          type: 'Read Model',
          cardId: 'card_2',
          dataFields: [
            {
              name: 'Field 1',
              cardinality: null,
              exampleData: ['Example data', 'Example data', 'Example data'],
              relatedEntityId: 'entity_1'
            },
            {
              name: 'Field 2',
              cardinality: null,
              exampleData: null,
              relatedEntityId: null
            }
          ]
        }
      ]
    },
    {
      id: 'event_2',
      description: 'Example description 2',
      laneId: 'lane_2',
      parentEventNames: ['Example description 1'],
      cards: [
        {
          id: 'card_3',
          description: 'Card 3',
          cardType: {
            id: 'card_type_1',
            title: 'Card type 1'
          }
        },
        {
          id: 'card_4',
          description: 'Card 4',
          cardType: {
            id: 'card_type_2',
            title: 'Card type 2'
          }
        }
      ]
    }
  ],
  lanes: [
    { id: 'lane_1', name: 'Lane 1' },
    { id: 'lane_2', name: 'Lane 2' }
  ],
  entities: [
    {
      id: 'entity_1',
      name: 'Entity 1',
      dataFields: [
        {
          name: 'Field 1',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: true,
          reference: null,
          relatedEntityId: null,
          isRequired: true
        },
        {
          name: 'Field 2',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: false,
          reference: true,
          relatedEntityId: 'entity_2',
          isRequired: false
        }
      ]
    },
    {
      id: 'entity_2',
      name: 'Entity 2',
      dataFields: [
        {
          name: 'Field 1',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: true,
          reference: null,
          relatedEntityId: null,
          isRequired: true
        },
        {
          name: 'Field 2',
          cardinality: 'one-to-many',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: false,
          reference: true,
          relatedEntityId: 'entity_3',
          isRequired: false
        }
      ]
    }
  ]
}

export const exampleFileTagsOutput = {
  fileTags: [
    {
      path: 'src/controllers/OrderController.js',
      tags: ['command-uuid1', 'readmodel-uuid2']
    },
    {
      path: 'src/models/Customer.js',
      tags: ['entity-uuid3']
    },
    {
      path: 'src/services/ProductQueryService.java',
      tags: ['readmodel-uuid4']
    }
  ]
}

export const workflowDescriptionExamples = [
  'The incident management process for ITIL version 3',
  'A manufacturing process for outdoor clothing',
  'An order process used by e-commerce platform similar to Amazon',
  'A process for last mile delivery for an electronics retailer',
  'A KYC process for onboarding a large corporate bank customer',
  'A process for ordering home delivery of food',
  'A recruitment process for engineers used by Google',
  'A CRM process used by Salesforce'
]

export const getEventExample = (
  exampleData,
  cardTypes,
  options = {}
) => {
  const { role, name, commandExample, readModelExample, queriedEntity, id, conditionLabel } = exampleData
  const { shouldAddCards = true, shouldAddDataModels = true } = options
  let exampleObj
  if (id) {
    // used when adding to an existing event (with known id)
    exampleObj = { id }
  } else {
    // used when generating a new event (without known id)
    exampleObj = { role, name, conditionLabel }
  }

  const commandCardType = diagramHelpers.methods.getCommandCardType(cardTypes)
  const queryCardType = diagramHelpers.methods.getQueryCardType(cardTypes)

  if (shouldAddDataModels && commandExample && commandCardType?.generateWithAI) {
    exampleObj.dataFields = commandExample
  }

  if (shouldAddDataModels && readModelExample && queryCardType?.generateWithAI) {
    exampleObj.readModelDataFields = readModelExample
    exampleObj.queriedEntity = queriedEntity
  }

  if (shouldAddCards && cardTypes?.length) {
    cardTypes.forEach((cardType) => {
      if (cardType.generateWithAI && cardType.title) {
        const key = formatCardKey(cardType.title)
        exampleObj[key] = `Example description for ${cardType.title.toLowerCase()} X`
      }
    })
  }

  return exampleObj
}
